import React, {
  useState,
  useCallback,
  useMemo,
  useContext,
  useEffect
} from 'react';
import { withModal } from '@tmobile/tmo-bps-syncup-web-component-lib';

import Fetcher from '../Fetcher';
import ThingHeader from './ThingHeader';
import ThingActions from './ThingActions';
import ThingsList from './ThingsList';

import thingService from '../../services/thingService';
import deviceService from '../../services/deviceService';

import ActiveUserTabContext from '../../context/ActiveUserTabContext';
import StatusContext from '../../context/StatusContext';

import { APP_MOBILE } from '../../utils/app_constants';
import { getRandomString } from '../../utils/stringUtils';
import { getTimestampRangeForLastHours } from '../../utils/dateUtils';
import { trackCustomEvents } from '../../utils/appInsight_analytics';

function Things({
  openModal,
  thingsCount,
  productType,
  thingTypeName,
  refresh
}) {
  const {
    selectedTab: { userId, product }
  } = useContext(ActiveUserTabContext);

  const { addSuccessMessage, addErrorMessage } = useContext(StatusContext);

  const [currentThingId, setCurrentThingId] = useState();
  const [things, setThings] = useState();
  const [lastRefresh, setLastRefresh] = useState(getRandomString());
  const [firstUpdate] = useState(refresh);

  const handleManualRefresh = () => setLastRefresh(getRandomString());

  const actionCallback = useCallback(
    () =>
      thingService.getThingsByUserId({
        userId,
        syncUpAppName: product
      }),
    [userId, product]
  );

  const handleSetThings = useCallback(things => {
    setCurrentThingId(null);
    setThings(things);
  }, []);

  const activeThings = useMemo(
    () => things?.filter(t => t.status !== 'DELETED'),
    [things]
  );

  useEffect(() => {
    refresh !== firstUpdate && handleManualRefresh();
  }, [refresh, firstUpdate]);

  useEffect(() => {
    things && thingsCount(things.length);
  }, [things, thingsCount]);

  const thing = useMemo(() => {
    const safe = activeThings || [];
    return safe.find(t => String(t.id) === String(currentThingId)) || safe[0];
  }, [currentThingId, activeThings]);

  const deletedThingsCount = useMemo(
    () => things?.filter(t => t.status === 'DELETED').length,
    [things]
  );

  const handleRemoveDevice = async ({ deviceId, thingId }) => {
    setThings(prev =>
      prev.map(t => (t.id === thingId ? { ...t, currentDevice: null } : t))
    );

    try {
      await deviceService.unregisterDevice({
        userId,
        thingId,
        deviceId,
        syncUpAppName: product
      });
      setThings(await actionCallback());
      addSuccessMessage({
        message: `${thing.currentDevice.id} was successfully deleted.`
      });
    } catch (error) {
      addErrorMessage({ error });
    }
  };

  const handleAddDevice = async ({
    thingId,
    hardwareId,
    product,
    model,
    lastFourIcc
  }) => {
    try {
      await thingService.linkDevice({
        thingId,
        hardwareId,
        product,
        model,
        lastFourIcc,
        userId,
        syncUpAppName: product
      });

      setThings(await actionCallback());
      addSuccessMessage({
        message: `Onboarding started for ${hardwareId}`
      });
    } catch (error) {
      addErrorMessage({ error });
    }
  };

  const returnThingObject = ({ formValues }) => {
    switch (product) {
      case APP_MOBILE.PETS:
        return formValues;

      case APP_MOBILE.TRACKER:
        return {
          name: formValues.name,
          notes: formValues.notes,
          thingType: formValues.thingType,
          trackerType: formValues.trackerType,
          locationRateProfile: 'CUSTOM',
          locationRateValueInSeconds: 10,
          deviceLinkOperation: {
            kind: 'REGISTER',
            identifier: formValues.identifier,
            product: 'TRACKER',
            model: 'TAG1'
          }
        };

      case APP_MOBILE.WATCH:
        return {
          name: formValues.name,
          notes: formValues.notes,
          thingType: 'WATCH',
          watchType: 'CHILD',
          locationRateProfile: 'CUSTOM',
          locationRateValueInSeconds: 10,
          relation: formValues.relation,
          gender: formValues.gender,
          birthdate: formValues.birthdate,
          coppa: {
            version: 3,
            modifiedDateTime: getTimestampRangeForLastHours(14 * 24).endTime
          },
          deviceLinkOperation: {
            kind: 'REGISTER',
            identifier: formValues.identifier,
            product: 'KIDSWATCH',
            model: 'WATCH1',
            lastFourIcc: formValues.lastFourIcc
          }
        };

      case APP_MOBILE.FAMILYLOCATION:
        return {
          name: formValues.name,
          notes: formValues.notes,
          thingType: formValues.thingType,
          deviceLinkOperation: null,
          lineId: formValues.lineId,
          displayName: formValues.displayName,
          realtimeNotifications: false
        };

      default:
        return null;
    }
  };

  const openAddThingModal = async () => {
    const { isConfirmed, formValues } = await openModal();

    if (isConfirmed) {
      trackCustomEvents(`Add Thing - ${thingTypeName}`, {
        userId,
        syncUpAppName: product
      });

      try {
        await thingService.addThing({
          thing: returnThingObject({ formValues }),
          userId,
          syncUpAppName: product
        });

        setThings(await actionCallback());
        addSuccessMessage({
          message: `${formValues.name} was successfully added!`
        });
      } catch (error) {
        addErrorMessage({ error });
      }
    }
  };

  return (
    <Fetcher
      action={actionCallback}
      onLoad={handleSetThings}
      key={lastRefresh}
      render={() => (
        <>
          <ThingHeader
            things={things}
            activeThings={activeThings}
            deletedThingsCount={deletedThingsCount}
            thingTypeName={thingTypeName}
            setCurrentThingId={setCurrentThingId}
            thingActions={
              <ThingActions
                thing={thing}
                thingTypeName={thingTypeName}
                handleManualRefresh={handleManualRefresh}
                openAddThingModal={openAddThingModal}
                productType={productType}
              />
            }
          />

          <ThingsList
            thing={thing}
            things={things}
            onAddDevice={handleAddDevice}
            onRemoveDevice={handleRemoveDevice}
            setThings={setThings}
            actionCallback={actionCallback}
            userId={userId}
            product={product}
            addSuccessMessage={addSuccessMessage}
            addErrorMessage={addErrorMessage}
            productType={productType}
            thingTypeName={thingTypeName}
          />
        </>
      )}
    />
  );
}

export default withModal(Things);
